import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FaFlask, FaBookOpen, FaUsers, FaMicroscope, FaFileAlt } from 'react-icons/fa';
import './ResearchPage.css';

const ResearchPage: React.FC = () => {
  return (
    <div className="research-page">
      {/* Hero */}
      <section className="research-hero">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1>Medical Research Support: Become an Author in a Medical Journal</h1>
            <p className="research-subtitle">
              Publication-focused research guidance for high school, undergraduate pre-med, and medical students looking to boost their application competitiveness. Work one-on-one with Dr. Eghrari to produce an independent research project and publish your work.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Why Research */}
      <section className="research-section">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            <h2>Why Research Publication Strengthens Your Academic Profile</h2>
            <p className="research-body">
              Research output has become increasingly important in applicant competitiveness in medical education. Serious engagement in research — especially projects developed into publications — demonstrates intellectual curiosity, scientific reasoning, and academic maturity. These are qualities competitive undergraduate programs, medical schools, and residency programs value highly. Students with posters, presentations, and publications are automatically viewed as ready for the academic rigor in medical school and beyond.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Researcher Credentials */}
      <section className="research-section credentials-section">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            <h2>Research Support Led by an Active Medical Researcher</h2>
            <p className="research-body">
              This program is led by a current resident physician with a strong track record of peer-reviewed research and national academic presentations.
            </p>
            <ul className="credentials-list">
              <li><FaFlask className="cred-icon" /> Author of 15+ peer-reviewed medical journal publications</li>
              <li><FaBookOpen className="cred-icon" /> Author of 40+ scientific abstracts</li>
              <li><FaMicroscope className="cred-icon" /> Worked with prestigious institutions such as Cleveland Clinic, Barrow Neurological Institute, and Johns Hopkins</li>
              <li><FaFileAlt className="cred-icon" /> First-author research featured in national media coverage, including The Wall Street Journal, Medscape, Newsweek, Fox News, and New York Post</li>
              <li><FaUsers className="cred-icon" /> Presenter at national medical and scientific conferences across the U.S.</li>
              <li><FaMicroscope className="cred-icon" /> Ongoing involvement in clinical and outcomes research projects</li>
              <li><FaBookOpen className="cred-icon" /> 5+ years of experience writing, revising, and submitting manuscripts through peer review</li>
            </ul>
          </motion.div>
        </div>
      </section>

      {/* Who This Is For */}
      <section className="research-section">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            <h2>Who This Program Is Best For</h2>
            <ul className="audience-list">
              <li>Research-motivated high school students or BS/MD applicants</li>
              <li>Pre-med undergraduates</li>
              <li>Honors or thesis track students</li>
              <li>Gap year applicants</li>
              <li>Medical students interested in academic medicine</li>
            </ul>
          </motion.div>
        </div>
      </section>

      {/* What Students Receive */}
      <section className="research-section">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            <h2>What Students Receive</h2>
            <ul className="receive-list">
              <li>One-on-one collaboration with an experienced medical student researcher</li>
              <li>Research topic and question development</li>
              <li>Study design, literature review, and manuscript structure guidance</li>
              <li>Section-by-section writing feedback</li>
              <li>Line-by-line editing for clarity and scientific tone</li>
              <li>Journal selection guidance</li>
              <li>Submission-ready manuscript/poster preparation support</li>
              <li>Template for scientific poster and PowerPoint for oral presentation</li>
            </ul>
          </motion.div>
        </div>
      </section>

      {/* Process */}
      <section className="research-section process-section">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            <h2>How the Publication Mentorship Process Works</h2>
            <div className="process-steps">
              {[
                { step: "1", title: "Research Strategy Session", desc: "Define your interests, goals, and identify a feasible research direction." },
                { step: "2", title: "Project Framework & Outline", desc: "Build a structured plan including study design and methodology." },
                { step: "3", title: "Guided Writing & Review Cycles", desc: "Draft each section with structured feedback and iterative revisions." },
                { step: "4", title: "Manuscript Refinement", desc: "Line-by-line editing, data interpretation support, and scientific tone review." },
                { step: "5", title: "Submission Preparation", desc: "Journal selection, formatting, cover letter, and submission guidance." },
              ].map((item, i) => (
                <motion.div
                  key={i}
                  className="process-step"
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.4, delay: i * 0.1 }}
                  viewport={{ once: true }}
                >
                  <div className="step-number">{item.step}</div>
                  <div className="step-content">
                    <h3>{item.title}</h3>
                    <p>{item.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Pricing */}
      <section className="research-section pricing-section">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            <h2>Research Support Services</h2>
            <div className="research-pricing-grid">
              {/* Hourly */}
              <motion.div
                className="research-pricing-card"
                whileHover={{ y: -5, boxShadow: '0 10px 30px rgba(0,0,0,0.12)' }}
              >
                <h3>Hourly Research Advising & Writing</h3>
                <div className="research-price">$79/hour</div>
                <p className="pricing-desc">One-on-one consultation with an experienced medical researcher to get any questions answered, regardless of what stage your research is at. Students may use this for:</p>
                <ul>
                  <li>Manuscript review & editing</li>
                  <li>Poster creation and presentation practice</li>
                  <li>Research topic brainstorming</li>
                  <li>Advising on how to find research opportunities and apply for research internships</li>
                </ul>
                <div className="research-comparable">Comparable large advising companies: $150–$300 per hour</div>
              </motion.div>

              {/* Starter */}
              <motion.div
                className="research-pricing-card"
                whileHover={{ y: -5, boxShadow: '0 10px 30px rgba(0,0,0,0.12)' }}
              >
                <h3>Research Starter Track</h3>
                <div className="research-price">$549</div>
                <p className="pricing-desc">Best for students who need a jumpstart into a research project and guidance on how to design a project, analyze data, write a manuscript, and prepare for a presentation/publication. Students receive:</p>
                <ul>
                  <li>Research topic and question development</li>
                  <li>Literature search guidance</li>
                  <li>Manuscript outline and research writing framework templates</li>
                  <li>3 complete presentation and/or manuscript reviews</li>
                  <li>Submission pathway guidance</li>
                  <li>Weekly mentorship check-ins</li>
                  <li>Timeline: ~4 weeks (more as needed)</li>
                </ul>
                <div className="research-comparable">Comparable large advising companies: $1500+</div>
              </motion.div>

              {/* Comprehensive */}
              <motion.div
                className="research-pricing-card"
                whileHover={{ y: -5, boxShadow: '0 10px 30px rgba(0,0,0,0.12)' }}
              >
                <h3>Comprehensive Publication Track</h3>
                <div className="research-price">$1,399</div>
                <p className="pricing-desc">Designed for students who want full mentorship from study design through a publication-ready manuscript. Ideal for students committed to producing a submission-ready research paper with structured authorship guidance, iterative feedback, and advanced manuscript development support. Students receive:</p>
                <ul>
                  <li>Everything in Starter Track</li>
                  <li>Full guidance in project initiation and manuscript development</li>
                  <li>Complete manuscript drafting guidance and line-by-line editing</li>
                  <li>Data analysis and interpretation support</li>
                  <li>Unlimited presentation and/or manuscript reviews until submission</li>
                  <li>Journal selection and targeting strategy</li>
                  <li>Submission formatting and preparation guidance</li>
                  <li>Cover letter and submission materials support</li>
                  <li>Reviewer revision strategy guidance (if feedback is received)</li>
                  <li>Text/email support and weekly mentorship check-ins</li>
                  <li>Timeline: ~10 weeks (more as needed)</li>
                </ul>
                <div className="research-comparable">Comparable large advising companies: $4000+</div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="research-cta">
        <div className="container">
          <motion.div
            className="cta-content"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            <h2>Next Steps</h2>
            <p>Book a Free Research Strategy Call to determine the best program track and research pathway for your goals.</p>
            <Link to="/contact" className="btn btn-primary">Book a Free Research Strategy Call</Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ResearchPage;
